import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import cn from 'classnames'
import { message } from 'antd'
import requests from '../../../axios/requests'
import useTrajectory from './useTrajectory'
import { pluralN, skillStatus } from './genUtils'
import { flyToPlan, freshDelay } from './fly'
import s from './gen.module.scss'

export const STEPS = [
  { key: 'prof-review', n: 1, title: 'Обзор профессий', desc: 'Выберите профессию, добавьте навыки' },
  { key: 'generate-course', n: 2, title: 'Курс или тест', desc: 'ИИ предложит варианты плана' },
  { key: 'courses', n: 3, title: 'Созданные', desc: 'Готовые курсы и материалы' },
]

// Поле «+ Добавить навык» с подсказками из справочника навыков.
export const SkillAdd = () => {
  const { add, has } = useTrajectory()
  const [value, setValue] = useState('')
  const [items, setItems] = useState([])
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState(null)
  const timer = useRef(null)
  const fieldRef = useRef(null)

  // подсказки поверх страницы: поле стоит в свёрнутой полосе навыков, которая обрезает содержимое
  useLayoutEffect(() => {
    if (!open || !fieldRef.current) return
    const r = fieldRef.current.getBoundingClientRect()
    setPos({ left: r.left, top: r.bottom + 6, width: Math.max(r.width, 240) })
  }, [open, items])

  // при прокрутке подсказки закрываем — они привязаны к экрану, а не к полю
  useEffect(() => {
    if (!open) return
    const el = document.getElementById('element_for_scroll_up')
    const close = () => setOpen(false)
    el && el.addEventListener('scroll', close, { passive: true })
    return () => el && el.removeEventListener('scroll', close)
  }, [open])

  useEffect(() => {
    clearTimeout(timer.current)
    const q = value.trim()
    if (q.length < 2) {
      setItems([])
      return
    }
    timer.current = setTimeout(() => {
      requests.profs
        .get_speciality_tags(q, 1, 10)
        .then(({ data }) => setItems(data?.results || []))
        .catch(() => setItems([]))
    }, 400)
    return () => clearTimeout(timer.current)
  }, [value])

  const pick = (name) => {
    if (has(name)) message.warning('Этот навык уже в учебном плане')
    else {
      flyToPlan(fieldRef.current, name)
      add(name)
    }
    setValue('')
    setItems([])
    setOpen(false)
  }

  return (
    <div className={s.addSkill}>
      <label className={s.addField} ref={fieldRef}>
        <span aria-hidden="true">+</span>
        <input
          value={value}
          placeholder="Добавить навык"
          aria-label="Добавить навык в учебный план"
          onChange={(e) => {
            setValue(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && items[0]) pick(items[0].name)
          }}
        />
      </label>
      {open && items.length > 0 && pos && (
        <div
          className={s.dropdown}
          role="listbox"
          style={{ position: 'fixed', left: pos.left, top: pos.top, right: 'auto', width: pos.width }}
        >
          {items.map((it) => (
            <button type="button" key={it.id} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(it.name)}>
              {it.name}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// Полоса навыков, свёрнутая до двух рядов; «Показать все» раскрывает её, если навыки не помещаются.
// footer — действия в той же третьей строке (шаг 1: «Добавить навык» и «Сгенерировать»).
// clamp — три ряда «таблеток» (34px) с запасом под их тень
export const ChipsBox = ({ count, footer, clamp = 130, children, style, ...rest }) => {
  const ref = useRef(null)
  const [open, setOpen] = useState(false)
  const [over, setOver] = useState(false)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const check = () => setOver(el.scrollHeight > clamp + 4)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  })

  return (
    <div className={s.chipsBox}>
      <div
        ref={ref}
        className={s.planChips}
        style={{ ...style, ...(open ? null : { maxHeight: clamp, overflow: 'hidden' }) }}
        {...rest}
      >
        {children}
      </div>
      {(over || footer) && (
        <div className={s.chipsFoot}>
          {over && (
            <button
              type="button"
              className={s.linkbtn}
              aria-expanded={open}
              data-plan-more=""
              onClick={() => setOpen(!open)}
            >
              {open ? 'Свернуть' : `Показать все · ${count}`}
            </button>
          )}
          {footer && <div className={s.chipsActions}>{footer}</div>}
        </div>
      )}
    </div>
  )
}

// Чип навыка в полосе учебного плана.
// plain — вид прежней «Корзины скилов» (шаг 1): только название и крестик, без статуса.
export const PlanChip = ({ skill, removable, onRemove, plain }) => {
  const st = skillStatus(skill)
  // только что добавленный навык появляется, когда к нему «прилетает» копия
  const [landAt] = useState(() => freshDelay(skill.name))
  return (
    <span
      className={cn(s.pchip, { [s.pchipFresh]: landAt !== null })}
      data-plan-chip={(skill.name || '').trim().toLowerCase()}
      style={{ ...(!removable ? { paddingRight: 14 } : null), ...(landAt ? { animationDelay: `${landAt}ms` } : null) }}
    >
      {!plain && <span className={s.dot} style={{ background: st.dot }} />}
      <span>{skill.name}</span>
      {!plain && <span className={cn(s.muted, s.small)}>{st.meta}</span>}
      {removable && (
        <button type="button" className={s.pchipX} aria-label={`Убрать «${skill.name}» из плана`} onClick={onRemove}>
          ×
        </button>
      )}
    </span>
  )
}

// Строка шагов. Пройденные шаги показывают итог, текущий — подсказку.
export const StepsNav = ({ active, onGo, summary }) => {
  const idx = STEPS.findIndex((x) => x.key === active)
  return (
    <nav className={s.steps} aria-label="Шаги генератора">
      {STEPS.map((st, i) => {
        const done = i < idx && summary[i]
        return (
          <button
            type="button"
            key={st.key}
            className={cn(s.step, { [s.stepCur]: i === idx, [s.stepDone]: done })}
            aria-current={i === idx ? 'step' : undefined}
            onClick={() => onGo(st.key)}
          >
            <span className={s.stepN}>{done ? '✓' : st.n}</span>
            <span style={{ minWidth: 0 }}>
              <b>{st.title}</b>
              <span className={s.stepD} style={done ? { display: 'block' } : null}>
                {done ? summary[i] : st.desc}
              </span>
            </span>
          </button>
        )
      })}
    </nav>
  )
}

export const stepSummary = (list) => {
  const withCourse = list.filter((x) => skillStatus(x).kind === 'course').length
  return [
    list.length ? `${pluralN(list.length, ['навык', 'навыка', 'навыков'])} в учебном плане` : '',
    withCourse ? `${pluralN(withCourse, ['навык', 'навыка', 'навыков'])} с курсом` : '',
    '',
  ]
}
